import React, {
  Fragment,
  TouchEvent as ReactTouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Transition } from '@headlessui/react'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import { Case, Switch, When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { CarouselDots } from '@/atoms/CarouselDots/CarouselDots'
import { GuildScore } from '@/atoms/GuildScore'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { PlayCircleOutlineIcon } from '@/atoms/Icons/PlayCircleOutlineIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { ClosedCaptionsBadge, ContentRatingBadge, HighDefinitionBadge } from '@/atoms/MetadataBadge'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { CaptionMD, CaptionSM, LabelMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { projectTypes } from '@/constants/projectTypes'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { BoundedImage } from '@/molecules/BoundedImage'
import { GuildRecommendedAge } from '@/molecules/GuildRecommendedAge'
import { WatchlistButton } from '@/molecules/WatchlistButton'
import {
  ContentInteraction,
  DiscoverItemInteraction,
  HOVER_THRESHOLD_MS,
} from '@/services/Analytics/ContentInteractions'
import { getRatingForContent } from '@/services/ContentRatingService'
import { STREAMING_CONTENT_TYPES, StreamingContentType } from '@/services/RecommendedBannerService'
import { HeroBannerData } from '@/services/RecommendedBannerService/types'
import { useUser } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useWatchPageContext } from '../WatchPageContext'

interface RecommendedBannerCarouselProps {
  heroBanners: HeroBannerData[]
  className?: string
  createInteraction?: (banner: HeroBannerData, itemIndex: number) => DiscoverItemInteraction | null
  trackInteraction?: (interaction: ContentInteraction, allowAutoImpression?: boolean) => void
}

export const RecommendedBannerCarouselFull: React.FC<RecommendedBannerCarouselProps> = ({
  heroBanners,
  className,
  createInteraction,
  trackInteraction,
}) => {
  const { isLoggedIn } = useUser()
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const minSwipeDistance = 200
  const contentRef = useRef<HTMLDivElement>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const { t } = useTranslate('watch')
  const { setCurrentBanner, currentBanner } = useWatchPageContext()
  const track = useSafeTrack()

  // Interaction tracking refs
  const [bannerRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9,
  })
  const hoverStartTimeRef = useRef<number | null>(null)
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)

  const guildJoinText = useMemo(() => {
    if (isLoggedIn) {
      return t('unlock&Watch', 'Unlock & Watch')
    }

    return t('unlockAndWatch', 'Unlock and Watch')
  }, [isLoggedIn, t])

  const trackClick = useCallback(
    (recommendationId: string, isGuildCTA: boolean) => {
      if (isGuildCTA) {
        track('Clicked Guild Banner CTA', { recommendationId })
      }

      // Track content interaction
      const currentBanner = heroBanners[currentSlide]
      if (currentBanner && createInteraction && trackInteraction) {
        const interaction = createInteraction(currentBanner, currentSlide)
        if (interaction) {
          const pressInteraction = interaction.withPress()
          trackInteraction(pressInteraction)
        }
      }
    },
    [track, heroBanners, currentSlide, createInteraction, trackInteraction],
  )

  const handleMouseEnter = useCallback(() => {
    hoverStartTimeRef.current = Date.now()
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (!hoverStartTimeRef.current || !createInteraction || !trackInteraction) return

    const dwellTime = Date.now() - hoverStartTimeRef.current
    const currentBanner = heroBanners[currentSlide]

    if (dwellTime >= HOVER_THRESHOLD_MS && currentBanner) {
      const interaction = createInteraction(currentBanner, currentSlide)
      if (interaction) {
        const hoverInteraction = interaction.withHover(dwellTime)
        trackInteraction(hoverInteraction)
      }
    }

    hoverStartTimeRef.current = null
  }, [heroBanners, currentSlide, createInteraction, trackInteraction])

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === heroBanners.length - 1 ? 0 : prev + 1))
  }, [heroBanners.length])

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? heroBanners.length - 1 : prev - 1))
  }, [heroBanners.length])

  const onTouchStart = (event: ReactTouchEvent) => {
    setTouchEnd(0)
    const firstTouch = event.targetTouches[0]
    if (firstTouch) {
      setTouchStart(firstTouch.clientX)
    }
  }

  const onTouchMove = (event: ReactTouchEvent) => {
    const firstTouch = event.targetTouches[0]
    if (firstTouch) {
      setTouchEnd(firstTouch.clientX)
    }
  }

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd

    if (distance > minSwipeDistance) {
      nextSlide()
    } else if (distance < -minSwipeDistance) {
      prevSlide()
    }
  }, [touchStart, touchEnd, nextSlide, prevSlide])

  useEffect(() => {
    const interval = setInterval(() => {
      if (currentSlide === (heroBanners?.length ?? 0) - 1) {
        setCurrentSlide(0)
      } else {
        setCurrentSlide(currentSlide + 1)
      }
    }, 6500)
    return () => clearInterval(interval)
  }, [currentSlide, heroBanners?.length])

  // Arrow key navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isVisible) return

      if (event.key === 'ArrowLeft') {
        prevSlide()
      } else if (event.key === 'ArrowRight') {
        nextSlide()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [nextSlide, prevSlide, isVisible])

  useEffect(() => {
    setCurrentBanner(heroBanners?.[currentSlide] ?? null)
  }, [currentSlide, heroBanners, setCurrentBanner])

  // Track impression when visible for at least 1 second
  useEffect(() => {
    // Reset impression tracking for new slide
    hasTrackedImpression.current = false

    if (!createInteraction || !trackInteraction) return

    const currentBanner = heroBanners[currentSlide]
    if (!currentBanner) return

    // Clear any existing timeout
    if (impressionTimeoutRef.current) {
      clearTimeout(impressionTimeoutRef.current)
      impressionTimeoutRef.current = null
    }

    if (isVisible) {
      impressionTimeoutRef.current = setTimeout(() => {
        if (!hasTrackedImpression.current) {
          hasTrackedImpression.current = true
          const interaction = createInteraction(currentBanner, currentSlide)
          if (interaction) {
            const impressionInteraction = interaction.withImpression()
            trackInteraction(impressionInteraction)
          }
        }
      }, 1000)
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [isVisible, heroBanners, currentSlide, createInteraction, trackInteraction])

  const ctaButtonProps = useMemo(() => {
    const banner = heroBanners[currentSlide]
    if (!banner) return { ctaText: '', icon: null, href: '/', isGuildCTA: false }

    const hasGuildAction = (actions?: Array<{ __typename?: string | null } | null | undefined> | null): boolean =>
      Array.isArray(actions) && actions.some((a) => a?.__typename === 'GuildAvailabilityAction')

    if (banner.type === 'tickets') {
      return {
        ctaText: t('getShowtimes', 'Get Showtimes'),
        icon: <TicketIcon size={22} color={'core-gray-950'} />,
        href: `${paths.tickets.index}/${banner.project?.slug}`,
        isGuildCTA: false,
      }
    }

    if (banner.type === 'streaming') {
      const { title } = banner.project || {}
      const isStreamingContent = STREAMING_CONTENT_TYPES.includes(title?.__typename as StreamingContentType)

      if (isStreamingContent) {
        const actionsToCheck =
          title?.__typename === 'ContentSeries' ? title?.firstWatchable?.actionsToWatch : title?.actionsToWatch

        if (hasGuildAction(actionsToCheck)) {
          return {
            ctaText: guildJoinText,
            icon: <GuildIconMono size={22} color={'core-gray-950'} />,
            href: `${paths.guild.join}/${banner.project?.slug}`,
            isGuildCTA: true,
          }
        }

        return {
          ctaText: t('watchNow', 'Watch Now'),
          icon: <PlayCircleOutlineIcon color={'core-gray-950'} />,
          href: `${paths.watch.index}/${banner.project?.slug}`,
          isGuildCTA: false,
        }
      }
    }

    return {
      ctaText: banner.genericConfig?.cta || '',
      icon: null,
      href: banner?.genericConfig?.link ? banner.genericConfig.link : `${paths.watch.index}/${banner.project?.slug}`,
      isGuildCTA: false,
    }
  }, [currentSlide, guildJoinText, heroBanners, t])

  const logoPath = useMemo(() => {
    const banner = heroBanners[currentSlide]
    if (!banner) return ''

    if (banner.type === 'generic') {
      return (
        banner?.genericConfig?.logoCloudinaryPath ??
        banner?.project?.title?.logo?.cloudinaryPath ??
        banner?.project?.logoCloudinaryPath
      )
    } else {
      return banner?.project?.title?.logo?.cloudinaryPath ?? banner?.project?.logoCloudinaryPath
    }
  }, [currentSlide, heroBanners])

  return (
    <div
      className="group-watch-banner relative -mx-4 flex flex-col sm:-mx-8 md:-mx-12 xl:-mx-16"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={bannerRef}
    >
      <Fragment key={currentSlide}>
        <Transition
          show
          appear
          as="div"
          enter="transition-opacity duration-1000"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-5500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={classNames([
              'flex flex-col gap-4 relative w-full lg:items-center min-h-[60vh] lg:flex-row lg:flex-wrap md:min-h-[45vh] lg:min-h-[50vh] xl:min-h-[53vh] 2xl:min-h-[630px] bg-transparent',
              className,
            ])}
            ref={contentRef}
          >
            <PaddedContainer fluid className="flex items-center justify-center">
              <div className="relative mt-[23vh] w-full pt-24 md:mt-6 lg:mt-10 lg:max-w-[600px] xl:max-w-[650px]">
                <BoundedImage
                  className="mb-4 flex min-h-[64px] items-center justify-center md:mb-2 md:justify-start"
                  alt={currentBanner?.project?.name ?? ''}
                  path={logoPath}
                  width={600}
                  height={104}
                  imageClassName="se:max-h-[64px]"
                />
                <div className="mb-2 lg:mb-4">
                  <CaptionMD
                    className="min-h-9 text-center md:max-w-[420px] md:text-left lg:min-h-[18px] lg:max-w-[600px] xl:max-w-[650px]"
                    weight="medium"
                  >
                    {currentBanner?.flavorText}
                  </CaptionMD>
                </div>
                <div className="mb-2 flex flex-row flex-wrap items-center justify-center gap-1 md:justify-start lg:mb-4 lg:gap-2">
                  {currentBanner?.project?.releaseDate && (
                    <>
                      <CaptionSM className="sm:photon-caption-md">
                        {DateTime.fromISO(currentBanner?.project?.releaseDate).year}
                      </CaptionSM>
                      <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                    </>
                  )}
                  <CaptionSM className="sm:photon-caption-md whitespace-nowrap">
                    <Switch>
                      <Case condition={currentBanner?.project?.projectType === projectTypes.series}>
                        <Translate i18nKey="tvSeries" t={t}>
                          TV Series
                        </Translate>
                      </Case>
                      <Case condition={currentBanner?.project?.projectType === projectTypes.movie}>
                        <Translate i18nKey="movie" t={t}>
                          Movie
                        </Translate>
                      </Case>
                      <Case condition={currentBanner?.project?.projectType === projectTypes.podcast}>
                        <Translate i18nKey="podcast" t={t}>
                          Podcast
                        </Translate>
                      </Case>
                      <Case condition={currentBanner?.project?.projectType === projectTypes.special}>
                        <Translate i18nKey="special" t={t}>
                          Special
                        </Translate>
                      </Case>
                    </Switch>
                  </CaptionSM>
                  {(currentBanner?.project?.title?.genres?.length ?? 0) > 0 && (
                    <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                  )}
                  {currentBanner?.project?.title?.genres?.map((genre: string | null, index: number) => {
                    return (
                      <div className="flex gap-1 lg:gap-2" key={genre}>
                        <CaptionSM className="sm:photon-caption-md">{genre}</CaptionSM>
                        <When condition={index < (currentBanner?.project?.title?.genres?.length ?? 0) - 1}>
                          <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                        </When>
                      </div>
                    )
                  })}
                </div>
                <div className="md:min-h-auto mb-4 flex min-h-[47px] flex-row flex-wrap items-center justify-center gap-2 md:justify-start lg:mb-8 lg:min-h-7">
                  {currentBanner?.project?.slug && (
                    <GuildRecommendedAge
                      projectSlug={currentBanner.project.slug}
                      iconSize={23}
                      recommendedAge={currentBanner?.project?.title?.recommendedAge?.age}
                      contentRatings={currentBanner?.project?.title?.contentRatings}
                      compact
                      placement="top"
                    />
                  )}
                  {currentBanner?.project?.title?.contentRatings?.length && (
                    <ContentRatingBadge
                      contentRating={getRatingForContent({
                        __typename: currentBanner?.project?.title?.__typename,
                        contentRatings: currentBanner?.project?.title?.contentRatings,
                      })}
                      className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
                    />
                  )}
                  {currentBanner?.project?.slug && (
                    <HighDefinitionBadge
                      slug={currentBanner?.project?.slug}
                      className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
                    />
                  )}
                  <ClosedCaptionsBadge className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl" />
                  <When condition={currentBanner?.project?.highestScore}>
                    <GuildScore guildScore={currentBanner?.project?.highestScore} hideAnimate />
                  </When>
                </div>
                <div className="relative flex w-full flex-row items-start justify-start gap-4">
                  <LinkButton
                    href={ctaButtonProps?.href ?? ''}
                    variant="white"
                    onClick={() =>
                      trackClick(currentBanner?.recommendationId ?? '', ctaButtonProps?.isGuildCTA ?? false)
                    }
                    className="flex max-h-12 w-full items-center gap-[6px] rounded-xl px-6 py-[14px] md:w-fit"
                  >
                    <LabelMD
                      weight="semibold"
                      className="mt-[2.25px] flex flex-row items-center justify-center gap-2"
                      color="core-gray-950"
                    >
                      {ctaButtonProps?.icon}
                      {ctaButtonProps?.ctaText}
                    </LabelMD>
                  </LinkButton>
                  {currentBanner?.project?.title?.id && currentBanner?.project?.slug && isLoggedIn && (
                    <WatchlistButton titleId={currentBanner.project.title.id} />
                  )}
                </div>
              </div>
            </PaddedContainer>
          </div>
        </Transition>
      </Fragment>
      <div className="mt-4 flex w-full items-center justify-center md:mt-0">
        <CarouselDots
          count={heroBanners.length}
          currentIndex={currentSlide}
          onDotClick={setCurrentSlide}
          className="min-w-[98px] md:items-end md:pb-2"
        />
      </div>
      <div className="absolute bottom-2 right-0 hidden min-w-10 lg:mx-12 lg:flex xl:mx-16">
        <div className="relative opacity-0 transition-all duration-300 ease-in-out group-watch-banner-hover:opacity-100">
          <div className="flex flex-row gap-4">
            <Button
              variant="plain"
              onClick={prevSlide}
              className={classNames(
                'pointer-events-auto rounded-full p-2 bg-core-gray-900 opacity-55 hover:opacity-100 transition-all ease-in-out duration-300',
              )}
              aria-label={`Scroll left`}
            >
              <CaretRightIcon size={26} color={'white'} className="rotate-180" />
            </Button>
            <Button
              variant="plain"
              onClick={nextSlide}
              className={classNames(
                'pointer-events-auto rounded-full p-2 bg-core-gray-900 opacity-55 hover:opacity-100 transition-all ease-in-out duration-300',
              )}
              aria-label={`Scroll right`}
            >
              <CaretRightIcon size={26} color={'white'} className="" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
